// ============================================================================
// UPGRADEMENU.JS — Upgrade purchase UI for Palm Island (pi-t0j.6)
// ============================================================================
import { UPGRADE_DEFS, purchaseUpgrade, hasUpgrade } from '../systems/upgrades.js';
import { canAfford, spendResources, getInventory, RESOURCE_DEFS } from '../systems/resources.js';
import { eventBus, GameEvents } from '../systems/events.js';

let button = null;
let overlay = null;
let isOpen = false;

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
function injectStyles() {
  const style = document.createElement('style');
  style.textContent = `
    /* ---- Palm tree button (bottom-right, above hammer) ---- */
    #upgrade-btn {
      position: fixed;
      bottom: 76px;
      right: 16px;
      z-index: 900;
      background: linear-gradient(135deg, #f5e6c8 0%, #e8d5a3 100%);
      border: 2px solid #c4a265;
      border-radius: 12px;
      padding: 8px 14px;
      font-family: 'Georgia', serif;
      font-size: 15px;
      color: #5a3e1b;
      cursor: pointer;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
      display: flex;
      align-items: center;
      gap: 6px;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
      user-select: none;
      -webkit-tap-highlight-color: transparent;
    }
    #upgrade-btn:hover {
      transform: scale(1.05);
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.25);
    }
    #upgrade-btn:active {
      transform: scale(0.97);
    }

    /* ---- Modal overlay ---- */
    #upgrade-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 950;
      background: rgba(0, 0, 0, 0.6);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      display: flex;
      justify-content: center;
      align-items: center;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.3s ease;
    }
    #upgrade-overlay.open {
      opacity: 1;
      pointer-events: auto;
    }

    /* ---- Panel ---- */
    #upgrade-panel {
      background: linear-gradient(145deg, #faf0d7 0%, #f0deb0 50%, #e8d5a3 100%);
      border: 3px solid #c4a265;
      border-radius: 20px;
      width: 90%;
      max-width: 480px;
      max-height: 80vh;
      overflow-y: auto;
      padding: 28px 24px;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
      font-family: 'Georgia', serif;
      color: #5a3e1b;
      transform: scale(0.9);
      transition: transform 0.3s ease;
    }
    #upgrade-overlay.open #upgrade-panel {
      transform: scale(1);
    }
    #upgrade-panel::-webkit-scrollbar {
      width: 6px;
    }
    #upgrade-panel::-webkit-scrollbar-thumb {
      background: #c4a265;
      border-radius: 3px;
    }

    /* ---- Header ---- */
    .upgrade-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      border-bottom: 2px solid #c4a265;
      padding-bottom: 12px;
    }
    .upgrade-title {
      font-size: 24px;
      font-weight: bold;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .upgrade-close {
      background: none;
      border: 2px solid #c4a265;
      border-radius: 50%;
      width: 36px;
      height: 36px;
      font-size: 20px;
      color: #5a3e1b;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s;
      font-family: 'Georgia', serif;
    }
    .upgrade-close:hover {
      background: #c4a265;
      color: #fff;
    }

    /* ---- Upgrade card list ---- */
    .upgrade-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    /* ---- Upgrade card ---- */
    .upgrade-card {
      background: linear-gradient(135deg, #fff8e7 0%, #f5e6c8 100%);
      border: 2px solid #d4b87a;
      border-radius: 14px;
      padding: 16px;
      display: flex;
      gap: 14px;
      align-items: flex-start;
      transition: transform 0.15s ease, box-shadow 0.15s ease;
    }
    .upgrade-card.available {
      border-color: #a0853a;
    }
    .upgrade-card.available:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(160, 133, 58, 0.3);
    }
    .upgrade-card.purchased {
      opacity: 0.65;
      border-color: #b8a878;
    }
    .upgrade-card.unaffordable {
      opacity: 0.55;
      border-color: #d4b87a;
    }

    .upgrade-card-icon {
      font-size: 36px;
      line-height: 1;
      flex-shrink: 0;
    }

    .upgrade-card-body {
      flex: 1;
      min-width: 0;
    }
    .upgrade-card-name {
      font-size: 16px;
      font-weight: bold;
      margin-bottom: 4px;
    }
    .upgrade-card-desc {
      font-size: 13px;
      line-height: 1.4;
      margin-bottom: 8px;
      opacity: 0.85;
    }

    /* ---- Cost row ---- */
    .upgrade-cost-row {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-bottom: 8px;
    }
    .upgrade-cost-item {
      font-size: 12px;
      white-space: nowrap;
      padding: 2px 6px;
      border-radius: 6px;
      background: rgba(0, 0, 0, 0.06);
    }
    .upgrade-cost-item.has-enough {
      color: #3a7a3a;
    }
    .upgrade-cost-item.not-enough {
      color: #a03a3a;
    }

    /* ---- Buy button ---- */
    .upgrade-buy-btn {
      background: linear-gradient(135deg, #a0853a 0%, #8a7030 100%);
      color: #fff;
      border: none;
      border-radius: 8px;
      padding: 6px 16px;
      font-family: 'Georgia', serif;
      font-size: 13px;
      font-weight: bold;
      cursor: pointer;
      transition: background 0.2s, transform 0.15s;
      user-select: none;
      -webkit-tap-highlight-color: transparent;
    }
    .upgrade-buy-btn:hover {
      background: linear-gradient(135deg, #b8973e 0%, #a08838 100%);
      transform: scale(1.03);
    }
    .upgrade-buy-btn:active {
      transform: scale(0.97);
    }
    .upgrade-buy-btn:disabled {
      background: #c4b99a;
      color: #8a8a7a;
      cursor: default;
      transform: none;
    }

    /* ---- Owned badge ---- */
    .upgrade-owned-badge {
      display: inline-block;
      background: #3a7a3a;
      color: #fff;
      border-radius: 8px;
      padding: 4px 12px;
      font-size: 12px;
      font-weight: bold;
    }

    /* ---- Shortage hint ---- */
    .upgrade-hint {
      font-size: 11px;
      font-style: italic;
      color: #a03a3a;
      margin-top: 4px;
    }

    /* ---- Empty state ---- */
    .upgrade-empty {
      text-align: center;
      padding: 30px 20px;
      font-size: 15px;
      opacity: 0.7;
      font-style: italic;
    }

    @media (max-width: 480px) {
      #upgrade-panel {
        padding: 20px 16px;
      }
      .upgrade-card {
        padding: 12px;
        gap: 10px;
      }
      .upgrade-card-icon {
        font-size: 28px;
      }
    }
  `;
  document.head.appendChild(style);
}

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------
function createButton() {
  button = document.createElement('button');
  button.id = 'upgrade-btn';
  button.innerHTML = '\uD83C\uDF34';
  button.addEventListener('click', () => {
    if (isOpen) closeMenu(); else openMenu();
  });
  document.body.appendChild(button);
}

// ---------------------------------------------------------------------------
// Overlay + panel
// ---------------------------------------------------------------------------
function createOverlay() {
  overlay = document.createElement('div');
  overlay.id = 'upgrade-overlay';
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeMenu();
  });
  document.body.appendChild(overlay);
}

// ---------------------------------------------------------------------------
// Render upgrade cards
// ---------------------------------------------------------------------------
function renderCards() {
  if (!overlay) return;

  const upgrades = Object.values(UPGRADE_DEFS);
  const inventory = getInventory();

  let html = `
    <div id="upgrade-panel">
      <div class="upgrade-header">
        <div class="upgrade-title">\uD83C\uDF34 Upgrades</div>
        <button class="upgrade-close" aria-label="Close">\u00D7</button>
      </div>
  `;

  if (upgrades.length === 0) {
    html += '<div class="upgrade-empty">No upgrades available yet.</div>';
  } else {
    html += '<div class="upgrade-list">';
    for (const def of upgrades) {
      const owned = hasUpgrade(def.id);
      const affordable = !owned && canAfford(def.cost);
      const cardClass = owned ? 'purchased' : affordable ? 'available' : 'unaffordable';

      html += `<div class="upgrade-card ${cardClass}" data-upgrade-id="${def.id}">`;
      html += `  <div class="upgrade-card-icon">${def.icon}</div>`;
      html += `  <div class="upgrade-card-body">`;
      html += `    <div class="upgrade-card-name">${def.name}</div>`;
      html += `    <div class="upgrade-card-desc">${def.description}</div>`;

      // Cost row
      html += '    <div class="upgrade-cost-row">';
      for (const [type, needed] of Object.entries(def.cost)) {
        const have = inventory[type] || 0;
        const resDef = RESOURCE_DEFS[type];
        const icon = resDef ? resDef.icon : type;
        const cls = have >= needed ? 'has-enough' : 'not-enough';
        html += `<span class="upgrade-cost-item ${cls}">${icon} ${needed}</span>`;
      }
      html += '    </div>';

      // Action area
      if (owned) {
        html += '<span class="upgrade-owned-badge">Owned \u2713</span>';
      } else if (affordable) {
        html += `<button class="upgrade-buy-btn" data-buy-id="${def.id}">Buy</button>`;
      } else {
        html += `<button class="upgrade-buy-btn" disabled>Buy</button>`;
        const hint = getShortageHint(def.cost, inventory);
        if (hint) {
          html += `<div class="upgrade-hint">${hint}</div>`;
        }
      }

      html += '  </div>'; // card-body
      html += '</div>';   // card
    }
    html += '</div>'; // upgrade-list
  }

  html += '</div>'; // upgrade-panel
  overlay.innerHTML = html;

  // --- Attach event listeners ---
  const closeBtn = overlay.querySelector('.upgrade-close');
  if (closeBtn) closeBtn.addEventListener('click', closeMenu);

  const buyButtons = overlay.querySelectorAll('.upgrade-buy-btn[data-buy-id]');
  buyButtons.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const upgradeId = btn.dataset.buyId;
      handlePurchase(upgradeId);
    });
  });
}

// ---------------------------------------------------------------------------
// Shortage hint
// ---------------------------------------------------------------------------
function getShortageHint(cost, inventory) {
  const shortages = [];
  for (const [type, needed] of Object.entries(cost)) {
    const have = inventory[type] || 0;
    if (have < needed) {
      const resDef = RESOURCE_DEFS[type];
      const name = resDef ? resDef.name : type;
      shortages.push(`${needed - have} more ${name}`);
    }
  }
  if (shortages.length === 0) return '';
  return `Need ${shortages.join(', ')}`;
}

// ---------------------------------------------------------------------------
// Purchase handler
// ---------------------------------------------------------------------------
function handlePurchase(upgradeId) {
  const def = UPGRADE_DEFS[upgradeId];
  if (!def) return;
  if (hasUpgrade(upgradeId)) return;
  if (!canAfford(def.cost)) return;

  const spent = spendResources(def.cost);
  if (!spent) return;

  const purchased = purchaseUpgrade(upgradeId);
  if (!purchased) return;

  // Re-render to show "Owned" state, then close
  renderCards();
  closeMenu();
}

// ---------------------------------------------------------------------------
// Update cards (called on inventory changes while menu is open)
// ---------------------------------------------------------------------------
function updateCards() {
  if (isOpen) renderCards();
}

// ---------------------------------------------------------------------------
// Open / close
// ---------------------------------------------------------------------------
function openMenu() {
  if (isOpen) return;
  isOpen = true;
  renderCards();
  overlay.classList.add('open');
}

function closeMenu() {
  if (!isOpen) return;
  isOpen = false;
  overlay.classList.remove('open');
}

// ---------------------------------------------------------------------------
// Public init
// ---------------------------------------------------------------------------
export function initUpgradeMenu() {
  injectStyles();
  createButton();
  createOverlay();

  eventBus.on(GameEvents.INVENTORY_CHANGED, updateCards);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen) closeMenu();
  });

  console.log('[UpgradeMenu] Initialized');
}
